import DimensionUnitsEnum from "@models/DimensionUnitsEnum";

// Conversion factors to base unit (meters)
const TO_METERS: { [key in DimensionUnitsEnum]: number } = {
    [DimensionUnitsEnum.INCHES]: 0.0254,
    [DimensionUnitsEnum.FEET]: 0.3048,
    [DimensionUnitsEnum.METERS]: 1,
    [DimensionUnitsEnum.CENTIMETERS]: 0.01,
    [DimensionUnitsEnum.MILLIMETERS]: 0.001,
    [DimensionUnitsEnum.YARDS]: 0.9144,
};

// Direct conversion factors for common conversions (for precision)
const INCHES_TO_FEET = 1 / 12;
const FEET_TO_INCHES = 12;
const CM_TO_INCHES = 1 / 2.54;
const INCHES_TO_CM = 2.54;

/**
 * Convert a dimension value from one unit to another
 * @param value The value to convert
 * @param fromUnit The unit to convert from
 * @param toUnit The unit to convert to
 * @returns The converted value
 */
export function convertDimension(value: number, fromUnit: DimensionUnitsEnum | undefined, toUnit: DimensionUnitsEnum | undefined): number {
    // If units are the same or undefined, return the original value
    if (!fromUnit || !toUnit || fromUnit === toUnit) {
        return value;
    }

    // Use direct conversions for common cases (better precision)
    if (fromUnit === DimensionUnitsEnum.INCHES && toUnit === DimensionUnitsEnum.FEET) {
        return value * INCHES_TO_FEET;
    }
    if (fromUnit === DimensionUnitsEnum.FEET && toUnit === DimensionUnitsEnum.INCHES) {
        return value * FEET_TO_INCHES;
    }
    if (fromUnit === DimensionUnitsEnum.INCHES && toUnit === DimensionUnitsEnum.CENTIMETERS) {
        return value * INCHES_TO_CM;
    }
    if (fromUnit === DimensionUnitsEnum.CENTIMETERS && toUnit === DimensionUnitsEnum.INCHES) {
        return value * CM_TO_INCHES;
    }

    // For other conversions, go through meters
    const meters = value * TO_METERS[fromUnit];
    return meters / TO_METERS[toUnit];
}

/**
 * Get the abbreviation for a dimension unit
 * @param unitId The dimension unit ID
 * @returns The abbreviation string
 */
export function getDimensionUnitAbbreviation(unitId: DimensionUnitsEnum | undefined): string {
    switch (unitId) {
        case DimensionUnitsEnum.INCHES:
            return "in";
        case DimensionUnitsEnum.FEET:
            return "ft";
        case DimensionUnitsEnum.METERS:
            return "m";
        case DimensionUnitsEnum.CENTIMETERS:
            return "cm";
        case DimensionUnitsEnum.MILLIMETERS:
            return "mm";
        case DimensionUnitsEnum.YARDS:
            return "yd";
        default:
            return "";
    }
}

/**
 * Determine if a unit is metric or imperial
 * @param unitId The dimension unit ID
 * @returns "metric" or "imperial"
 */
export function getUnitSystem(unitId: DimensionUnitsEnum | undefined): "metric" | "imperial" {
    switch (unitId) {
        case DimensionUnitsEnum.INCHES:
        case DimensionUnitsEnum.FEET:
        case DimensionUnitsEnum.YARDS:
            return "imperial";
        case DimensionUnitsEnum.METERS:
        case DimensionUnitsEnum.CENTIMETERS:
        case DimensionUnitsEnum.MILLIMETERS:
            return "metric";
        default:
            return "imperial"; // Default to imperial
    }
}

/**
 * Format a dimension value for display (e.g., 12.5ft -> 12'6")
 * @param value The dimension value to format
 * @param sourceUnit The unit of the input value
 * @param targetUnit The desired display unit (optional, defaults to appropriate unit for the system)
 * @returns The formatted display string
 */
export function formatDimensionDisplay(value: number, sourceUnit: DimensionUnitsEnum | undefined): string {
    if (!sourceUnit || value === 0) {
        return "";
    }

    const unitSystem = getUnitSystem(sourceUnit);

    if (unitSystem === "imperial") {
        // Convert to inches for calculation
        let totalInches: number;

        if (sourceUnit === DimensionUnitsEnum.FEET) {
            totalInches = value * FEET_TO_INCHES;
        } else if (sourceUnit === DimensionUnitsEnum.INCHES) {
            totalInches = value;
        } else if (sourceUnit === DimensionUnitsEnum.YARDS) {
            totalInches = value * 36; // 1 yard = 36 inches
        } else {
            // Convert from metric to inches if needed
            totalInches = convertDimension(value, sourceUnit, DimensionUnitsEnum.INCHES);
        }

        // Calculate feet and inches
        const feet = Math.floor(totalInches / 12);
        const inches = totalInches % 12;

        // Format the display
        if (feet === 0) {
            return `${inches.toFixed(inches % 1 === 0 ? 0 : 2)}"`;
        } else if (inches === 0) {
            return `${feet}'`;
        } else {
            // Format inches with up to 2 decimal places, removing trailing zeros
            const formattedInches = inches % 1 === 0 ? inches.toString() : inches.toFixed(2).replace(/\.?0+$/, "");
            return `${feet}'${formattedInches}"`;
        }
    } else {
        // Metric system
        let meters: number;

        if (sourceUnit === DimensionUnitsEnum.METERS) {
            meters = value;
        } else if (sourceUnit === DimensionUnitsEnum.CENTIMETERS) {
            meters = value / 100;
        } else if (sourceUnit === DimensionUnitsEnum.MILLIMETERS) {
            meters = value / 1000;
        } else {
            // Convert from imperial to meters if needed
            meters = convertDimension(value, sourceUnit, DimensionUnitsEnum.METERS);
        }

        // For values less than 1 meter, display in centimeters
        if (meters < 1) {
            const cm = meters * 100;
            return `${cm.toFixed(cm % 1 === 0 ? 0 : 2)}cm`;
        } else {
            // Display in meters
            return `${meters.toFixed(meters % 1 === 0 ? 0 : 2)}m`;
        }
    }
}
